import { Box, Flex, Text } from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { Link } from "@tanstack/react-router"
import { useTranslation } from "react-i18next"

import { OpenAPI } from "@/client"
import { useDemoMode } from "@/hooks/useDemoMode"

type Status = { enabled: boolean; required: boolean }

function fetchStatus(): Promise<Status | null> {
  const token = localStorage.getItem("access_token")
  if (!token) return Promise.resolve(null)
  const base = (OpenAPI.BASE || "").replace(/\/$/, "")
  return fetch(`${base}/api/v1/2sv/status`, {
    headers: { Authorization: `Bearer ${token}` },
  })
    .then((r) => (r.ok ? (r.json() as Promise<Status>) : null))
    .catch(() => null)
}

/**
 * Soft, app-wide 2SV nudge. Shows only when the org policy *requires* this
 * user to enroll and they haven't. Shares the ["2sv-status"] query key with the
 * settings panel, so enrolling clears the banner automatically.
 */
export function TwoSvNudge() {
  const { t } = useTranslation("settings")
  const { enabled: demoEnabled } = useDemoMode()
  const { data } = useQuery({
    queryKey: ["2sv-status"],
    queryFn: fetchStatus,
    // Demo visitors are never required to enroll, so skip the per-page query
    // entirely on demo clusters — that per-page DB hit, multiplied across many
    // concurrent demo visitors, was what exhausted the connection pool. For
    // real users, cache hard and don't refetch on focus/mount so it runs at
    // most once every few minutes per session.
    enabled: !demoEnabled,
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: false,
  })

  if (!data?.required) return null

  return (
    <Box
      mb={4}
      p={3}
      borderRadius="md"
      bg="orange.50"
      _dark={{ bg: "orange.900" }}
      borderWidth="1px"
      borderColor="orange.300"
    >
      <Flex align="center" justify="space-between" gap={3} flexWrap="wrap">
        <Text fontSize="sm">
          {t(
            "twoSv.requiredNudge",
            "Your administrator requires two-step verification.",
          )}
        </Text>
        <Link
          to="/settings"
          style={{ fontWeight: 600, color: "#34a1dd", whiteSpace: "nowrap" }}
        >
          {t("twoSv.setUpNow", "Set it up →")}
        </Link>
      </Flex>
    </Box>
  )
}
